import React, { useState, useEffect, useRef } from "react";
import {
  MapPin,
  Building2,
  Handshake,
  Truck,
  PackageCheck
} from "lucide-react";

const stats = [
  { value: 40, label: "Cities", icon: Building2 },
  { value: 8, label: "States", icon: MapPin },
  { value: 200, label: "Clients", icon: Handshake },
  { value: 75, suffix: "k+", label: "Vehicle Partners", icon: Truck },
  { value: 300, suffix: "k+", label: "Deliveries", icon: PackageCheck }
];

const HighlightStats = () => {
  const [counted, setCounted] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !counted) {
            setCounted(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [counted]);

  return (
    <section 
      ref={sectionRef}
      className="bg-gray-50/80 py-10 px-4 md:px-10"
    >
      <div className="w-full md:max-w-7xl mx-auto">
        <div className="text-center mb-4">
          <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900">
            Empowering businesses with{" "}
            <span className="bg-gradient-to-r from-indigo-800 to-blue-700 text-transparent bg-clip-text">
              safe, secure
            </span>{" "}
            and{" "}
            <span className="bg-gradient-to-r from-green-600 to-teal-700 text-transparent bg-clip-text">
              efficient
            </span>{" "}
            transportation
          </h2>
          <p className="text-sm md:text-base lg:text-lg font-medium mt-2 text-gray-600 max-w-3xl  mx-auto">
            Trusted by businesses across the country to deliver excellence in
            every mile
          </p>
        </div>

        <div className="grid grid-cols-3 md:grid-cols-3 lg:grid-cols-5 max-w-6xl mx-auto">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={index}
                className="p-6 transition-all flex flex-col items-center hover:-translate-y-1 hover:scale-105"
              >
                <Icon className="w-5 h-5 md:w-8 md:h-8 text-indigo-800 mb-3" />
                <p className="text-base md:text-xl lg:text-2xl font-bold bg-gradient-to-r from-indigo-900 to-blue-900 text-transparent bg-clip-text">
                  {counted ? (
                    <Counter target={stat.value} suffix={stat.suffix} />
                  ) : (
                    "0" + (stat.suffix || "")
                  )}
                </p>
                <p className="text-sm font-medium text-gray-500 mt-2 text-center">
                  {stat.label}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Counter component for the animation
const Counter = ({ target, suffix = "+" }) => {
  const [count, setCount] = useState(0);
  const duration = 2000; // animation duration in ms
  const steps = 50; // number of steps
  const stepTime = duration / steps;
  const increment = target / steps;

  useEffect(() => {
    let currentCount = 0;
    let stepsTaken = 0;
    
    const counter = setInterval(() => {
      currentCount += increment;
      stepsTaken++;
      
      if (stepsTaken >= steps) {
        currentCount = target;
        clearInterval(counter);
      }
      
      setCount(Math.floor(currentCount));
    }, stepTime);

    return () => clearInterval(counter);
  }, [target, increment, stepTime, steps]);

  return (
    <span>
      {count.toLocaleString()}
      {suffix}
    </span>
  );
};

export default HighlightStats;